import { atomWithObservable, atomWithStorage } from "jotai/utils";
import type { SyncStorage } from "jotai/vanilla/utils/atomWithStorage";
import type { Observable} from "rxjs";
import { BehaviorSubject, mergeMap, of } from "rxjs";
import type { IProductLeanDto } from "../../pages/ProductsListPage/dtos/product-lean/product-lean.dto.interface";
import type { IProductWithCmsDataAndQualification } from "../../pages/ProductsListPage/entities/product-with-cms-data-and-qualification/product-with-cms-data-and-qualification.entity.interface";
import { ProductSearchService } from "../../services/ProductService/ProductSearchService.service";
import type { ISearchResult } from "../../services/ProductService/interfaces/SearchResult.interface";
import { fetchProductsData } from "../../utils/fetchProductsData";
import { setProductsWithCashbackIntoGlobalState } from "./utils/setProductsWithCashbackIntoGlobalState";

export interface ICmsProductAtom {
  products: IProductLeanDto[];
}

export interface IServicesAtom {
  services: IProductWithCmsDataAndQualification[];
}

const defaultServicesAtom: IServicesAtom = {
  services: [],
};

const inMemoryStorageProductCollection: SyncStorage<IServicesAtom> = {
  getItem: function getItem(key: string) {
    return window.__PRODUCT_COLLECTION__ || defaultServicesAtom;
  },
  setItem: function setItem(key: string, newValue: IServicesAtom) {
    window.__PRODUCT_COLLECTION__ = { services: newValue.services };
  },
  removeItem: function removeItem(key: string) {
    window.__PRODUCT_COLLECTION__ = { services: [] };
  },
};

const PRODUCTS_COLLECTION_STORAGE_INDEX = "products-collection";

export const ProductsCollection = atomWithStorage<IServicesAtom>(
  PRODUCTS_COLLECTION_STORAGE_INDEX,
  defaultServicesAtom,
  inMemoryStorageProductCollection
);

ProductsCollection.onMount = function initialize(setAtom) {
  fetchProductsData().then(function retrieveProducts(products) {
    setAtom({ services: products });
    setProductsWithCashbackIntoGlobalState(products);
  });
};

export interface IProductsCitizenCollectionOptions<T> {
  filteredServices: T;
  uniqueFilteredServices: T;
  tagsName: string[];
  sectionPercetanges: number[];
}

export const productsCitizenCollection = new BehaviorSubject<
  IProductsCitizenCollectionOptions<IProductWithCmsDataAndQualification[]>
>({
  filteredServices: [],
  uniqueFilteredServices: [],
  tagsName: [],
  sectionPercetanges: [],
});

const productSearchService = new ProductSearchService();

export const productsCitizenCollectionFiltered: Observable<{ filteredProducts: ISearchResult }> =
  productsCitizenCollection.pipe(
    mergeMap(function retrieve(
      productsCitizenCollectionOptions: IProductsCitizenCollectionOptions<IProductWithCmsDataAndQualification[]>
    ) {
      const { filteredServices, uniqueFilteredServices, tagsName, sectionPercetanges } =
        productsCitizenCollectionOptions;

      const results = productSearchService.arrangeFilteredProducts<IProductWithCmsDataAndQualification, ISearchResult>(
        filteredServices,
        uniqueFilteredServices,
        tagsName,
        sectionPercetanges
      );

      const filteredProducts: ISearchResult = {
        purchasableServices: results.purchasableServices,
        needToCompleteInformationsServices: results.needToCompleteInformationsServices,
        needToUpdateProfileServices: results.needToUpdateProfileServices,
      };

      return of({ filteredProducts });
    })
  );

export const ProductsCitizenCollection = atomWithObservable<{ filteredProducts: ISearchResult }>(
  () => productsCitizenCollectionFiltered
);
